import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { authApi } from '@/api/auth'
import { setLocale } from '@/i18n'
import {
  clearAuthStorage,
  getCachedUser,
  getToken,
  setCachedUser,
  setToken,
} from '@/utils/authStorage'

/**
 * 登录状态。
 *
 * <p><b>这里的 role 只用来决定界面长什么样，不是权限控制。</b>
 * 用户可以在浏览器控制台里把 localStorage 的角色改成 OWNER，然后就能看到老板端的菜单——
 * 但每一个接口请求到了后端都会重新查库确认真实角色，所以他什么数据也拿不到。
 * 前端的角色判断是<i>体验</i>，后端的角色判断才是<i>安全</i>。混淆这两件事是
 * 前后端分离项目里最常见的安全漏洞来源。
 */
export const useAuthStore = defineStore('auth', () => {
  /** @type {import('vue').Ref<object | null>} */
  const user = ref(null)

  const token = ref('')

  /** 是否正在执行登录请求，用于禁用按钮、显示加载态。 */
  const loggingIn = ref(false)

  const isLoggedIn = computed(() => Boolean(token.value && user.value))
  const role = computed(() => user.value?.role ?? null)
  const isOwner = computed(() => role.value === 'OWNER')
  const isStaff = computed(() => role.value === 'STAFF')
  const isPlatformAdmin = computed(() => role.value === 'PLATFORM_ADMIN')

  /** 首次登录或密码被重置后，必须先改密码才能用其他功能。 */
  const mustChangePassword = computed(() => Boolean(user.value?.mustChangePassword))

  /**
   * 从 localStorage 恢复上次的登录状态。
   *
   * <p>必须在应用挂载前同步执行完，见 main.js。这里<b>刻意不去调用后端校验 token</b>：
   * 那会是一次网络往返，期间界面只能白屏等着。反正任何一个真实请求都会验证 token，
   * 失效时响应拦截器收到 401 会自动清理并跳登录页——用一次可能失败的渲染，
   * 换掉每次打开应用都要等的加载转圈，是划算的。
   */
  function restoreFromStorage() {
    const savedToken = getToken()
    const savedUser = getCachedUser()

    // 两者必须同时存在。只剩一半说明存储被部分清理过（比如用户手动删了一项），
    // 这种半残状态会让界面处于「有 token 但不知道你是谁」的诡异中间态。
    if (!savedToken || !savedUser) {
      clearAuthStorage()
      return
    }

    token.value = savedToken
    user.value = savedUser
  }

  /**
   * 登录。
   *
   * @param {{ username: string, password: string }} credentials
   * @throws {import('@/api/http').ApiError} 账号密码错误、账号或租户被停用
   */
  function applySession(result) {
    token.value = result.token
    user.value = {
      userId: result.userId,
      username: result.username,
      displayName: result.displayName,
      role: result.role,
      orgId: result.orgId,
      orgName: result.orgName,
      orgLoginCode: result.orgLoginCode,
      mustChangePassword: result.mustChangePassword,
    }

    setToken(result.token)
    setCachedUser(user.value)

    if (result.locale) {
      setLocale(result.locale)
    }

    return result
  }

  /**
   * 登录。
   *
   * @param {{ orgCode?: string | null, username: string, password: string }} credentials
   * @throws {import('@/api/http').ApiError} 账号密码错误、账号或租户被停用
   */
  async function login(credentials) {
    loggingIn.value = true
    try {
      return applySession(await authApi.login(credentials))
    } finally {
      loggingIn.value = false
    }
  }

  /**
   * 老板自助注册并自动登录。
   *
   * @param {{
   *   orgName: string,
   *   username: string,
   *   password: string,
   *   displayName: string,
   *   locale?: string,
   *   contactPhone?: string | null
   * }} payload
   */
  async function register(payload) {
    loggingIn.value = true
    try {
      return applySession(await authApi.register(payload))
    } finally {
      loggingIn.value = false
    }
  }

  /**
   * 退出登录。
   *
   * <p>没有对应的后端接口。token 是无状态的，服务端不保存会话，
   * 所以「退出」就是把本地的凭据丢掉。要做到服务端强制失效，需要维护一张撤销列表，
   * 对这个系统来说不值得——token 本身只有 12 小时有效期。
   */
  function logout() {
    token.value = ''
    user.value = null
    clearAuthStorage()
  }

  /**
   * 改密码成功后，把本地缓存里的强制改密标记清掉。
   *
   * <p>不清的话路由守卫会继续把用户拦在改密页，形成「改完了还让你改」的死循环。
   */
  function markPasswordChanged() {
    if (user.value) {
      user.value = { ...user.value, mustChangePassword: false }
      setCachedUser(user.value)
    }
  }

  return {
    user,
    token,
    loggingIn,
    isLoggedIn,
    role,
    isOwner,
    isStaff,
    isPlatformAdmin,
    mustChangePassword,
    restoreFromStorage,
    login,
    register,
    logout,
    markPasswordChanged,
  }
})
